import axios from './axiosConfig'
import { getEnv } from '@utils/env'

const AUTH_API_DOMAIN = getEnv('REACT_APP_AUTH_API_DOMAIN', '')

export interface CreatePatientFormsOtpParams {
  patient_id: string
  recipient: string
  ttl_seconds?: number
  appointment_id?: string
}

export interface CreatePatientFormsOtpResponse {
  otp_id: string
  expires_at: string
  code?: string
  invitation_token?: string
}

export interface VerifyOtpParams {
  code: string
  otp_id?: string
  invitation_token?: string
  patient_id?: string
  db_name?: string
  tenant_id?: string
}

export interface VerifyOtpResponse {
  access_token: string
  token_type: string
  expires_in: number
  scope: string
  patient_id: string
}

export interface ResolveInvitationResponse {
  valid: boolean
  expires_at: string
}

export async function createPatientFormsOtp(
  params: CreatePatientFormsOtpParams,
): Promise<CreatePatientFormsOtpResponse> {
  const { data } = await axios.post<{ data: CreatePatientFormsOtpResponse }>(
    `${AUTH_API_DOMAIN}/api/v1/auth/otp/patient-forms`,
    {
      patient_id: params.patient_id,
      recipient: params.recipient,
      ...(params.ttl_seconds != null && { ttl_seconds: params.ttl_seconds }),
      ...(params.appointment_id != null &&
        params.appointment_id !== '' && {
          appointment_id: params.appointment_id,
        }),
    },
    { headers: { 'Content-Type': 'application/json' } },
  )
  return data?.data ?? { otp_id: '', expires_at: '' }
}

export async function createPatientFormsInvitationCode(
  params: CreatePatientFormsOtpParams,
): Promise<CreatePatientFormsOtpResponse> {
  const { data } = await axios.post<{ data: CreatePatientFormsOtpResponse }>(
    `${AUTH_API_DOMAIN}/api/v1/auth/otp/invitation/patient-forms`,
    {
      patient_id: params.patient_id,
      recipient: params.recipient,
      ...(params.ttl_seconds != null && { ttl_seconds: params.ttl_seconds }),
      ...(params.appointment_id != null &&
        params.appointment_id !== '' && {
          appointment_id: params.appointment_id,
        }),
    },
    { headers: { 'Content-Type': 'application/json' } },
  )
  return data?.data ?? { otp_id: '', expires_at: '' }
}

export async function resolveInvitationToken(
  token: string,
): Promise<ResolveInvitationResponse> {
  const { data } = await axios.get<{ data: ResolveInvitationResponse }>(
    `${AUTH_API_DOMAIN}/api/v1/auth/otp/invitation/${encodeURIComponent(token)}`,
    { headers: { 'Content-Type': 'application/json' } },
  )
  const payload = data?.data
  if (!payload) throw new Error('Invalid invitation resolve response')
  return payload
}

export async function verifyOtp(
  params: VerifyOtpParams,
): Promise<VerifyOtpResponse> {
  const { data } = await axios.post<{ data: VerifyOtpResponse }>(
    `${AUTH_API_DOMAIN}/api/v1/auth/otp/verify`,
    {
      code: params.code,
      ...(params.otp_id != null &&
        params.otp_id !== '' && { otp_id: params.otp_id }),
      ...(params.invitation_token != null &&
        params.invitation_token !== '' && {
          invitation_token: params.invitation_token,
        }),
      ...(params.patient_id != null &&
        params.patient_id !== '' && { patient_id: params.patient_id }),
      ...(params.db_name != null &&
        params.db_name !== '' && { db_name: params.db_name }),
      ...(params.tenant_id != null &&
        params.tenant_id !== '' && { tenant_id: params.tenant_id }),
    },
    { headers: { 'Content-Type': 'application/json' } },
  )
  const payload = data?.data
  if (!payload) throw new Error('Invalid verify response')
  return payload
}

export const verifyInvitationCode = verifyOtp
